import { FunctionalComponent } from '@stencil/core';
import decode from 'unescape';
import warn from '../../../capabilities/warn';
import { iconTemplate } from '../external-icon/_external-icon';

/**
 * A common link decorator shared by link-text-basic, link-text-tertiary and call-to-action
 * @param props: LinkDecoratorProps
 * @returns null
 */

interface LinkDecoratorProps {
  // required props
  host: HTMLElement; // host element
  newWindowText: string; //  text to append to aria-label is target is "_blank"

  // optional props
  iconName?: string; // icon name
  iconSize?: string; // icon size
  role?: string; // role attribute
  classList?: string; // optional class names to add to the anchor, space separated
}

/**
 * Get the existing icon from the anchor, or create one if it doesn't exist
 * @param anchor
 * @returns
 */
const getIcon = (anchor: HTMLAnchorElement) => {
  const icon = anchor.querySelector('hp6-icon');
  if (icon) {
    return icon;
  }
  const newIcon = document.createElement('hp6-icon');
  anchor.insertBefore(newIcon, anchor.firstChild);
  return newIcon;
};

export const decorateAnchor = (props: LinkDecoratorProps) => {
  const { host, newWindowText, iconName = null, iconSize = 'sm', classList = '' } = props;

  const anchor = host.querySelector('a');

  if (anchor) {
    // get original classes before decoration marker class
    const [originalClasses] = anchor.className.split('decorated');
    anchor.className = originalClasses.trim();
    // add new classes after marker class
    if (classList) {
      anchor.classList.add('decorated');
      classList.split(' ').map(cls => anchor.classList.add(cls));
    }

    // anchor data-suffix no longer supported
    if (anchor.getAttribute('data-suffix')) {
      warn(
        'COMPONENT LIBRARY WARNING: a.data-suffix was deprecated in V5 and is not supported in V6',
      );
    }
    const firstChild = anchor.firstChild as HTMLElement;

    // prevent critical error if anchor has no children
    if (!firstChild) {
      return null;
    }

    if (firstChild.tagName === 'HP6-ICON' && !firstChild.hasAttribute('size-selection')) {
      firstChild.setAttribute('size-selection', iconSize);
    }
    if (iconName) {
      const icon = getIcon(anchor);
      icon.setAttribute('name', iconName);
      icon.setAttribute('size-selection', iconSize);
      anchor.insertBefore(icon, anchor.firstChild);
    }
    if (
      anchor.getAttribute('target') === '_blank'
      && !host.querySelector('hp6-icon[name="system-link-external"]')
    ) {
      anchor.appendChild(iconTemplate.content.cloneNode(true));
    }
    const textContent = decode(anchor.textContent).trim().replaceAll('\n', '').replace(/\s+/g, ' ');
    const ariaLabel = `${anchor.ariaLabel || textContent}${
      anchor.getAttribute('target') === '_blank' ? newWindowText : ''
    }`;
    if (ariaLabel && textContent !== ariaLabel) {
      anchor.setAttribute('aria-label', ariaLabel);
    }
    if (anchor.getAttribute('href') === '#' && anchor.onclick) {
      warn(
        `${host.tagName.toLowerCase()}: Anchor href is set to #, this is not a best practice for accessibility. To create a button, use the button component instead.`,
      );
    }
    anchor.setAttribute('data-decorated', '');
  }

  return anchor;
};

const LinkDecorator: FunctionalComponent<LinkDecoratorProps> = props => {
  decorateAnchor(props);
  return null;
};

export default LinkDecorator;
